import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, CircleCheck, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, getServiceErrorPayload } from "../../lib/api";
import {
  listActiveServiceTechnicians,
  moveServiceTicket,
  normalizeServiceCommandResponse,
} from "../../lib/serviceApi";
import { SERVICE_DVT_STATUS_LABELS, SERVICE_STAGE_LABELS } from "../../lib/serviceConstants";
import {
  deriveVoidWarrantyControlState,
  serviceOperationalDateFromTimestamp,
} from "../../lib/serviceWarranty";
import { FlowpiDialogSurface } from "../ui/flowpi-dialog";
import { FormError } from "../ui/feedback";
import { Button } from "../ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import SparePartSelector from "./SparePartSelector";
import CannibalPartSelector from "./CannibalPartSelector";
import VoidWarrantySwitch from "./VoidWarrantySwitch";
import ProtectedResolutionField from "./ProtectedResolutionField";
import { CompactServiceCostSection } from "./ServiceCostSection";
import { buildServiceCostMutation, serviceCostDraft, validateServiceCostDraft } from "../../lib/serviceCost";
import { canMutateServiceCost } from "../../lib/servicePermissions";

export const STANDARD_STAGE_TRANSITIONS = {
  incoming: ["queue", "pending"],
  queue: ["pending", "validation"],
  pending: ["queue"],
  validation: ["queue", "pending", "delivery", "done"],
  delivery: ["done"],
  done: [],
};

const REPAIR_ACTION_OPTIONS = [
  { value: "part_replace", label: "Part Replace" },
  { value: "part_repair", label: "Part Repair" },
  { value: "software_repair", label: "Software Repair" },
  { value: "cancel_service", label: "Cancel Service" },
];

export function getStandardStageTargets(stage) {
  return STANDARD_STAGE_TRANSITIONS[stage] || [];
}

const C4A_SOURCE_STAGES = new Set(["incoming", "queue", "pending"]);
const C4A_TRANSITIONS = new Set(["incoming->queue", "incoming->pending", "queue->pending", "pending->queue"]);

export function isC4AStandardMoveTransition(fromStage, toStage) {
  return C4A_TRANSITIONS.has(transitionKey(fromStage, toStage));
}

export function getInitialStandardMoveTarget(stage) {
  const targets = getStandardStageTargets(stage);
  if (stage === "validation") return "delivery";
  return C4A_SOURCE_STAGES.has(stage) && targets.length > 1 ? "" : targets.length === 1 ? targets[0] : "";
}

function usesInitialTargetChooser(stage) {
  return C4A_SOURCE_STAGES.has(stage) && getStandardStageTargets(stage).length > 1;
}

function transitionKey(fromStage, toStage) {
  return `${fromStage || ""}->${toStage || ""}`;
}

function requiresProblemRootCause(fromStage, toStage) {
  return ["queue->pending", "queue->validation"].includes(
    transitionKey(fromStage, toStage)
  );
}

function requiresValidationFields(fromStage, toStage) {
  return transitionKey(fromStage, toStage) === "queue->validation";
}

function usesValidationMoveForm(fromStage, toStage) {
  return requiresValidationFields(fromStage, toStage) || fromStage === "validation";
}

function requiresDeliveryDoneFields(fromStage, toStage) {
  return transitionKey(fromStage, toStage) === "delivery->done";
}

function isBlank(value) {
  return !String(value || "").trim();
}

export function calculateMoveUnderlyingDvtStatus(ticket) {
  const purchaseMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
    String(ticket?.device?.purchase_date || "")
  );
  const durationYears = ticket?.warranty?.dvt?.duration_years;
  const dateIn = serviceOperationalDateFromTimestamp(ticket?.date_in);
  if (!purchaseMatch || !Number.isInteger(durationYears) || durationYears < 1 || !dateIn) {
    return ticket?.warranty?.dvt?.status === "void" ? "" : ticket?.warranty?.dvt?.status;
  }
  const purchaseDate = new Date(
    Number(purchaseMatch[1]),
    Number(purchaseMatch[2]) - 1,
    Number(purchaseMatch[3])
  );
  const expiryDate = new Date(purchaseDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + durationYears);
  const dayDifference = Math.floor((dateIn - purchaseDate) / 86400000);
  if (dayDifference >= 0 && dayDifference <= 7) return "doa";
  return dateIn <= expiryDate ? "in_warranty" : "out_of_warranty";
}

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function initialForm(ticket) {
  return {
    problem: ticket?.problem || "",
    root_cause: ticket?.root_cause || "",
    resolution: ticket?.resolution || "",
    pending_reason: ticket?.pending_reason || "",
    repair_action: ticket?.repair_action || "",
    technician_id: ticket?.technician_id || "",
    notes: ticket?.notes || "",
    expedition: ticket?.expedition || "",
    tracking_number: ticket?.tracking_number || "",
    void_warranty: ticket?.warranty?.dvt?.status === "void",
    void_warranty_touched: false,
    void_reason: "",
  };
}

const initialSparePartSelection = {
  changed: false,
  finalUsedHoldIds: [],
  finalActiveUsageIds: [],
  loading: false,
  error: "",
  loaded: false,
  validationError: "",
  automaticReversal: false,
  summary: {
    holdsToConvert: [],
    usagesToReturn: [],
  },
};

export function normalizeStandardMoveReconciliationIds(values, fieldName) {
  if (!Array.isArray(values)) {
    throw new Error(`${fieldName} must be an array.`);
  }
  const normalized = values.map((value) => String(value || "").trim());
  if (normalized.some((value) => !value)) {
    throw new Error(`${fieldName} contains a blank ID.`);
  }
  if (new Set(normalized).size !== normalized.length) {
    throw new Error(`${fieldName} contains duplicate IDs.`);
  }
  return normalized.sort();
}

export function getStandardMoveReconciliationError(selection) {
  if (selection?.validationError) return selection.validationError;
  if (!selection?.changed) return "";
  try {
    normalizeStandardMoveReconciliationIds(selection.finalUsedHoldIds, "final_used_hold_ids");
    normalizeStandardMoveReconciliationIds(selection.finalActiveUsageIds, "final_active_usage_ids");
    return "";
  } catch (error) {
    return error.message;
  }
}

export function buildStandardMovePayload({
  currentStage,
  targetStage,
  form,
  originalForm,
  sparePartSelection,
  cannibalSelection = {},
  serviceCostMutation = {},
}) {
  const payload = { target_stage: targetStage };
  const notes = String(form.notes || "").trim();

  if (isC4AStandardMoveTransition(currentStage, targetStage)) {
    if (notes) payload.notes = notes;
    if (!isBlank(form.problem)) payload.problem = form.problem.trim();
    if (!isBlank(form.root_cause)) payload.root_cause = form.root_cause.trim();
    if (targetStage === "pending") payload.pending_reason = String(form.pending_reason || "").trim();
    return { ...payload, ...serviceCostMutation };
  }

  if (requiresValidationFields(currentStage, targetStage)) {
    payload.problem = form.problem.trim();
    payload.root_cause = form.root_cause.trim();
    payload.resolution = form.resolution;
    payload.repair_action = form.repair_action;
    payload.technician_id = form.technician_id;
    payload.expedition = String(form.expedition || "").trim();
    payload.notes = notes;
    if (form.void_warranty_touched) {
      payload.void_warranty = form.void_warranty;
      if (form.void_warranty) payload.void_reason = form.void_reason;
    }
    if (form.repair_action === "part_replace" && sparePartSelection.changed) {
      payload.final_used_hold_ids = normalizeStandardMoveReconciliationIds(
        sparePartSelection.finalUsedHoldIds,
        "final_used_hold_ids"
      );
      payload.final_active_usage_ids = normalizeStandardMoveReconciliationIds(
        sparePartSelection.finalActiveUsageIds,
        "final_active_usage_ids"
      );
    }
    if (form.repair_action === "part_replace" && cannibalSelection.enabled) {
      payload.cannibal_device_id = String(cannibalSelection.donorId || "").trim();
      payload.final_cannibal_item_ids = [...new Set(cannibalSelection.itemIds || [])].sort();
    }
  }
  if (currentStage === "validation") {
    const original = originalForm || form;
    const textFields = ["problem", "root_cause", "resolution", "pending_reason", "expedition", "notes"];
    textFields.forEach((field) => {
      const value = String(form[field] || "").trim();
      const originalValue = String(original[field] || "").trim();
      if (value !== originalValue) payload[field] = value;
    });
    ["repair_action", "technician_id"].forEach((field) => {
      const value = String(form[field] || "").trim() || null;
      const originalValue = String(original[field] || "").trim() || null;
      if (value !== originalValue) payload[field] = value;
    });
    if (form.repair_action === "part_replace" && sparePartSelection.changed) {
      payload.final_used_hold_ids = normalizeStandardMoveReconciliationIds(
        sparePartSelection.finalUsedHoldIds,
        "final_used_hold_ids"
      );
      payload.final_active_usage_ids = normalizeStandardMoveReconciliationIds(
        sparePartSelection.finalActiveUsageIds,
        "final_active_usage_ids"
      );
    }
    if (form.repair_action === "part_replace" && cannibalSelection.changed) {
      payload.cannibal_device_id = String(cannibalSelection.donorId || "").trim();
      payload.final_cannibal_item_ids = [...new Set(cannibalSelection.itemIds || [])].sort();
    }
  }
  if (requiresDeliveryDoneFields(currentStage, targetStage)) {
    if (notes) payload.notes = notes;
    payload.expedition = form.expedition.trim();
    payload.tracking_number = form.tracking_number.trim();
  }
  if (!usesValidationMoveForm(currentStage, targetStage) && !requiresDeliveryDoneFields(currentStage, targetStage) && notes) {
    payload.notes = notes;
  }
  return { ...payload, ...serviceCostMutation };
}

export function canEditStandardMoveServiceCost(role, sourceStage) {
  return canMutateServiceCost(role, sourceStage);
}

export function validateStandardMoveForm({ currentStage, targetStage, form }) {
  const errors = {};
  if (!targetStage) errors.target_stage = "Select a target stage.";
  if (requiresProblemRootCause(currentStage, targetStage)) {
    if (isBlank(form.problem)) errors.problem = "Problem is required.";
    if (isBlank(form.root_cause)) errors.root_cause = "Root Cause is required.";
  }
  if (requiresValidationFields(currentStage, targetStage)) {
    if (isBlank(form.resolution)) errors.resolution = "Resolution is required.";
    if (isBlank(form.repair_action)) errors.repair_action = "Repair Action is required.";
    if (isBlank(form.technician_id)) errors.technician_id = "Technician is required.";
    if (form.void_warranty_touched && form.void_warranty && isBlank(form.void_reason)) {
      errors.void_reason = "Void Reason is required.";
    }
  }
  if (targetStage === "pending" && isBlank(form.pending_reason)) {
    errors.pending_reason = "Pending Reason is required.";
  }
  if (requiresDeliveryDoneFields(currentStage, targetStage)) {
    if (isBlank(form.expedition)) errors.expedition = "Expedition is required.";
  }
  return errors;
}

export function serviceMoveFieldErrors(error) {
  const fieldErrors = getServiceErrorPayload(error)?.field_errors;
  if (!fieldErrors || typeof fieldErrors !== "object") return {};
  return Object.fromEntries(
    Object.entries(fieldErrors).filter(
      ([field, message]) => typeof field === "string" && typeof message === "string"
    )
  );
}

const IMIN_STATUS_LABELS = {
  valid: "Valid",
  invalid: "Invalid",
  unknown: "Unknown",
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : String(value);
}

function formatDateOnly(value) {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return displayValue(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function ReadOnlyMoveField({ label, value, labelAdornment = null }) {
  return (
    <div>
      <div className="flex min-h-5 items-center justify-between gap-3 text-xs font-semibold text-zinc-700 dark:text-zinc-300">
        <span>{label}</span>
        {labelAdornment}
      </div>
      <div className="mt-2 flex min-h-[44px] items-center justify-between rounded-sm border border-zinc-200 bg-zinc-100 px-3 py-2.5 text-sm text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
        <span>{displayValue(value)}</span>
        <Lock className="h-4 w-4 text-zinc-400" strokeWidth={1.8} aria-hidden="true" />
      </div>
    </div>
  );
}

export default function StageMoveDialog({ open, ticket, role, onClose, onMoved }) {
  const currentStage = ticket?.stage;
  const targets = useMemo(() => getStandardStageTargets(currentStage), [currentStage]);
  const usesTargetStep = usesInitialTargetChooser(currentStage);
  const [step, setStep] = useState(usesTargetStep ? "target" : "form");
  const [targetStage, setTargetStage] = useState(() => getInitialStandardMoveTarget(currentStage));
  const [form, setForm] = useState(() => initialForm(ticket));
  const [moveServiceCostDraft, setMoveServiceCostDraft] = useState(() => serviceCostDraft(ticket?.service_cost));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [techniciansError, setTechniciansError] = useState("");
  const [sparePartSelection, setSparePartSelection] = useState(initialSparePartSelection);
  const [cannibalSelection, setCannibalSelection] = useState({ donorId: "", itemIds: [], loading: false, error: "", loaded: false });
  const [pendingRepairAction, setPendingRepairAction] = useState(null);
  const submitInFlightRef = useRef(false);
  const sparePartPreviewInteractionRef = useRef(false);
  const moveAuthorized = currentStage !== "validation" || role === "admin";
  const canEditMoveServiceCost = canEditStandardMoveServiceCost(role, currentStage);

  const needsTechnician = usesValidationMoveForm(currentStage, targetStage);
  const needsSparePartSelection = usesValidationMoveForm(currentStage, targetStage);
  const automaticInventoryReversal = Boolean(
    needsSparePartSelection &&
      ticket?.repair_action === "part_replace" &&
      ["part_repair", "software_repair", "cancel_service"].includes(form.repair_action)
  );

  useEffect(() => {
    if (!open) return;
    setForm(initialForm(ticket));
    setMoveServiceCostDraft(serviceCostDraft(ticket?.service_cost));
    setTargetStage(getInitialStandardMoveTarget(currentStage));
    setStep(usesInitialTargetChooser(currentStage) ? "target" : "form");
    setErrors({});
    setSubmitting(false);
    submitInFlightRef.current = false;
    sparePartPreviewInteractionRef.current = false;
    setTechnicians([]);
    setTechniciansError("");
    setSparePartSelection(initialSparePartSelection);
    setCannibalSelection({ donorId: "", itemIds: [], loading: false, error: "", loaded: false });
    setPendingRepairAction(null);
  }, [currentStage, open, ticket]);

  useEffect(() => {
    if (!open || !needsTechnician || !moveAuthorized) return undefined;

    let cancelled = false;
    async function loadTechnicians() {
      setTechniciansLoading(true);
      setTechniciansError("");
      try {
        const response = await listActiveServiceTechnicians();
        if (cancelled) return;
        setTechnicians(unwrapList(response).filter((technician) => technician?.is_active === true));
      } catch (err) {
        if (cancelled) return;
        setTechnicians([]);
        setTechniciansError(formatApiError(err));
      } finally {
        if (!cancelled) setTechniciansLoading(false);
      }
    }

    loadTechnicians();

    return () => {
      cancelled = true;
    };
  }, [moveAuthorized, needsTechnician, open]);

  const handleSparePartStateChange = useCallback((state) => {
    setSparePartSelection({
      ...state,
      changed:
        state.changed ||
        (sparePartPreviewInteractionRef.current && Boolean(state.validationError)),
    });
  }, []);

  const markSparePartPreviewInteraction = (event) => {
    const button = event.target.closest?.("button");
    if (!button || button.disabled) return;
    if (["Add", "Remove"].includes(button.textContent.trim())) {
      sparePartPreviewInteractionRef.current = true;
    }
  };

  if (!open || !ticket || !moveAuthorized) return null;

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const { [field]: _removed, ...rest } = current;
      return rest;
    });
  };

  const applyRepairAction = (value) => {
    setField("repair_action", value);
    setPendingRepairAction(null);
  };

  const requestRepairAction = (value) => {
    const inventoryAffected = Boolean(
      sparePartSelection.changed ||
      sparePartSelection.finalUsedHoldIds?.length ||
      sparePartSelection.finalActiveUsageIds?.length
    );
    if (form.repair_action === "part_replace" && value !== "part_replace" &&
        (inventoryAffected || cannibalSelection.hasSelection)) {
      setPendingRepairAction(value);
      return;
    }
    applyRepairAction(value);
  };

  const validate = () => {
    const nextErrors = validateStandardMoveForm({ currentStage, targetStage, form });
    const reconciliationMustValidate =
      form.repair_action === "part_replace" &&
      (requiresValidationFields(currentStage, targetStage) ||
        (currentStage === "validation" && sparePartSelection.changed));
    if (reconciliationMustValidate) {
      const reconciliationValidationError = getStandardMoveReconciliationError(sparePartSelection);
      if (reconciliationValidationError) nextErrors.reconciliation = reconciliationValidationError;
    }
    if (form.repair_action === "part_replace" && cannibalSelection.enabled) {
      if (!cannibalSelection.donorId) nextErrors.cannibal_device_id = "Select a Cannibal donor.";
      if (!cannibalSelection.itemIds?.length) nextErrors.final_cannibal_item_ids = "Select at least one Item Master part.";
      if (cannibalSelection.error || !cannibalSelection.loaded) nextErrors.cannibal_selection = cannibalSelection.error || "Cannibal options are still loading.";
    }
    if (canEditMoveServiceCost) {
      const serviceCostError = validateServiceCostDraft(moveServiceCostDraft);
      if (serviceCostError) nextErrors.service_cost = serviceCostError;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    const serviceCostMutation = canEditMoveServiceCost
      ? buildServiceCostMutation(ticket?.service_cost, moveServiceCostDraft, ticket?.updated_at)
      : {};
    return buildStandardMovePayload({
      currentStage,
      targetStage,
      form,
      originalForm: initialForm(ticket),
      sparePartSelection,
      cannibalSelection,
      serviceCostMutation,
    });
  };

  const submit = async () => {
    if (submitInFlightRef.current) return;
    if (!validate()) return;
    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      const response = await moveServiceTicket(ticket.id, buildPayload());
      const commandResult = normalizeServiceCommandResponse(response);
      toast.success("Service ticket moved");
      onMoved?.(commandResult);
      onClose?.();
    } catch (err) {
      const fieldErrors = serviceMoveFieldErrors(err);
      setErrors((current) => ({
        ...current,
        ...fieldErrors,
        ...(Object.keys(fieldErrors).length === 0 ? { form: formatApiError(err) } : {}),
      }));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const selectTarget = (stage) => {
    setTargetStage(stage);
    setErrors({});
  };

  const continueToForm = () => {
    if (!targetStage) {
      setErrors({ target_stage: "Select a target stage." });
      return;
    }
    setErrors({});
    setStep("form");
  };

  const backToTargets = () => {
    setErrors({});
    setStep("target");
  };

  const errorText = (field) => (
    <p className="mt-1 min-h-[18px] text-xs text-destructive">{errors[field] || ""}</p>
  );

  const inputClass =
    "mt-2 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";
  const labelClass = "text-xs font-semibold text-zinc-700 dark:text-zinc-300";
  const showProblemRootCause = ["incoming->queue", "incoming->pending", "queue->pending", "queue->validation"].includes(
    transitionKey(currentStage, targetStage)
  );
  const showValidationFields = usesValidationMoveForm(currentStage, targetStage);
  const showVoidWarranty = requiresValidationFields(currentStage, targetStage);
  const showValidationTargetSelector = currentStage === "validation";
  const showDeliveryDoneFields = requiresDeliveryDoneFields(currentStage, targetStage);
  const selectorSafetyRequired =
    requiresValidationFields(currentStage, targetStage) && form.repair_action === "part_replace";
  const sparePartSelectionBlocked =
    selectorSafetyRequired &&
    (
      sparePartSelection.loading ||
      Boolean(sparePartSelection.error) ||
      !sparePartSelection.loaded ||
      Boolean(getStandardMoveReconciliationError(sparePartSelection))
    );
  const validationDependenciesBlocked =
    requiresValidationFields(currentStage, targetStage) &&
    (techniciansLoading || Boolean(techniciansError) || sparePartSelectionBlocked);
  const cannibalSelectionBlocked = form.repair_action === "part_replace" && cannibalSelection.enabled &&
    (cannibalSelection.loading || Boolean(cannibalSelection.error) || !cannibalSelection.loaded || !cannibalSelection.donorId || !cannibalSelection.itemIds?.length);
  const validationExitReconciliationBlocked =
    currentStage === "validation" &&
    form.repair_action === "part_replace" &&
    sparePartSelection.changed &&
    Boolean(sparePartSelection.validationError);
  const submitDisabled =
    submitting || targets.length === 0 || validationDependenciesBlocked || validationExitReconciliationBlocked || cannibalSelectionBlocked;
  const c4aForm = isC4AStandardMoveTransition(currentStage, targetStage);
  const stageLabel = (stage) => SERVICE_STAGE_LABELS[stage] || stage || "-";
  const reconciliationError =
    errors.final_used_hold_ids ||
    errors.final_active_usage_ids ||
    errors.reconciliation ||
    errors.spare_parts ||
    "";
  const historicalTechnician =
    form.technician_id && !technicians.some((technician) => technician.id === form.technician_id)
      ? [{ id: form.technician_id, name: ticket.technician_name || "Current technician", historical: true }]
      : [];
  const technicianOptions = [...historicalTechnician, ...technicians];
  const currentDvtStatus = ticket?.warranty?.dvt?.status;
  const underlyingDvtStatus = calculateMoveUnderlyingDvtStatus(ticket);
  const voidWarrantyControl = deriveVoidWarrantyControlState({
    busy: submitting,
    checked: form.void_warranty,
    currentStatus: currentDvtStatus,
    underlyingStatus: underlyingDvtStatus,
  });
  const updateVoidWarranty = ({ enabled, reason }) => {
    setForm((current) => ({
      ...current,
      void_warranty: enabled,
      void_warranty_touched: true,
      void_reason: enabled ? reason : "",
    }));
    setErrors((current) => {
      const { void_reason: _reason, void_warranty: _warranty, ...rest } = current;
      return rest;
    });
  };

  if (usesTargetStep && step === "target") {
    return (
      <FlowpiDialogSurface open={open} onClose={onClose} busy={submitting} size="confirmation" accessibleTitle="Choose Target Stage" testId="service-stage-move-dialog">
        <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex items-start justify-between border-b border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-header="">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                Service Workflow
              </div>
              <h2 className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Choose Target Stage
              </h2>
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                Move from {stageLabel(currentStage)}
              </p>
            </div>
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              size="icon"
              data-action-category="close"
              aria-label="Close Move dialog"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-6" data-flowpi-dialog-body="">
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Select exactly one target Stage to continue.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {targets.map((stage) => {
                const selected = targetStage === stage;
                return (
                  <button
                    key={stage}
                    type="button"
                    data-target-stage={stage}
                    aria-pressed={selected}
                    onClick={() => selectTarget(stage)}
                    className={`flex items-center justify-between rounded-sm border px-4 py-4 text-left text-sm font-semibold transition-colors ${
                      selected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-zinc-200 text-zinc-700 hover:border-primary/60 dark:border-zinc-700 dark:text-zinc-200"
                    }`}
                  >
                    {stageLabel(stage)}
                    <span
                      className={`h-4 w-4 rounded-full border ${
                        selected ? "border-4 border-primary" : "border-zinc-300 dark:border-zinc-600"
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                );
              })}
            </div>
            {errorText("target_stage")}
          </div>

          <div className="flex justify-end gap-2 border-t border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-footer="">
            <Button
              type="button"
              onClick={onClose}
              variant="cancel"
              data-action-category="cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={continueToForm}
              disabled={!targetStage}
              variant="navigation-forward"
              data-action-category="navigation-forward"
            >
              Continue
            </Button>
          </div>
        </div>
        <AlertDialog open={pendingRepairAction !== null} onOpenChange={(nextOpen) => !nextOpen && setPendingRepairAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Change Repair Action?</AlertDialogTitle><AlertDialogDescription>The spare-part draft will be hidden and preserved if you switch back before Submit. A final non-Part-Replace submit reverses active Usage.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => applyRepairAction(pendingRepairAction)}>Continue</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FlowpiDialogSurface>
    );
  }

  if (c4aForm) {
    return (
      <FlowpiDialogSurface open={open} onClose={onClose} busy={submitting} size="confirmation" accessibleTitle={`${stageLabel(currentStage)} to ${stageLabel(targetStage)}`} testId="service-stage-move-dialog">
        <div className="flex max-h-[calc(100vh-48px)] w-full max-w-xl flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex items-start justify-between border-b border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-header="">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                Service Workflow
              </div>
              <h2 className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                {stageLabel(currentStage)} to {stageLabel(targetStage)}
              </h2>
            </div>
            <Button
              type="button"
              onClick={onClose}
              disabled={submitting}
              variant="ghost"
              size="icon"
              data-action-category="close"
              aria-label="Close Move dialog"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5" data-flowpi-dialog-body="">
            <FormError>{errors.form}</FormError>
            <div>
              <label className={labelClass} htmlFor="service-move-problem">
                Problem
              </label>
              <textarea
                id="service-move-problem"
                value={form.problem}
                onChange={(event) => setField("problem", event.target.value)}
                disabled={submitting}
                placeholder="Describe problem reported..."
                className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("problem")}
            </div>
            <div>
              <label className={labelClass} htmlFor="service-move-root-cause">
                Root Cause
              </label>
              <textarea
                id="service-move-root-cause"
                value={form.root_cause}
                onChange={(event) => setField("root_cause", event.target.value)}
                disabled={submitting}
                placeholder="Describe root cause of the problem..."
                className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("root_cause")}
            </div>
            {targetStage === "pending" ? (
              <div>
                <label className={labelClass} htmlFor="service-move-pending-reason">
                  Pending Reason
                </label>
                <textarea
                  id="service-move-pending-reason"
                  value={form.pending_reason}
                  onChange={(event) => setField("pending_reason", event.target.value)}
                  disabled={submitting}
                  placeholder="Describe why this ticket is pending..."
                  className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
                />
                {errorText("pending_reason")}
              </div>
            ) : null}
            <div>
              <label className={labelClass} htmlFor="service-move-notes">
                Note
              </label>
              <textarea
                id="service-move-notes"
                value={form.notes}
                onChange={(event) => setField("notes", event.target.value)}
                disabled={submitting}
                placeholder="Describe Note"
                className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("notes")}
            </div>
            {canEditMoveServiceCost ? <CompactServiceCostSection value={ticket.service_cost} draft={moveServiceCostDraft} onChange={setMoveServiceCostDraft} editable={!submitting} error={errors.service_cost} /> : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-footer="">
            <Button
              type="button"
              onClick={backToTargets}
              disabled={submitting}
              variant="navigation-back"
              data-action-category="navigation-back"
            >
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={onClose}
                disabled={submitting}
                variant="cancel"
                data-action-category="cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={submitDisabled}
                variant="success"
                loading={submitting}
                loadingLabel="Submitting..."
                data-action-category="submit-confirm"
              >
                <CircleCheck className="h-4 w-4" strokeWidth={2} />
                Submit
              </Button>
            </div>
          </div>
        </div>
        <AlertDialog open={pendingRepairAction !== null} onOpenChange={(nextOpen) => !nextOpen && setPendingRepairAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Change Repair Action?</AlertDialogTitle><AlertDialogDescription>The spare-part draft will be hidden and preserved if you switch back before Submit. A final non-Part-Replace submit reverses active Usage.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => applyRepairAction(pendingRepairAction)}>Continue</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FlowpiDialogSurface>
    );
  }

  if (showDeliveryDoneFields) {
    return (
      <FlowpiDialogSurface open={open} onClose={onClose} busy={submitting} size="confirmation" accessibleTitle="Delivery to Done" testId="service-stage-move-dialog">
        <div className="flex max-h-[calc(100vh-48px)] w-full max-w-xl flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex items-start justify-between border-b border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-header="">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                Service Workflow
              </div>
              <h2 className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Delivery to Done
              </h2>
            </div>
            <Button
              type="button"
              onClick={onClose}
              disabled={submitting}
              variant="ghost"
              size="icon"
              data-action-category="close"
              aria-label="Close Move dialog"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5" data-flowpi-dialog-body="">
            <FormError>{errors.form}</FormError>
            <div data-delivery-move-field="expedition">
              <label className={labelClass} htmlFor="service-move-expedition">
                Expedition
              </label>
              <textarea
                id="service-move-expedition"
                value={form.expedition}
                onChange={(event) => setField("expedition", event.target.value)}
                disabled={submitting}
                placeholder="Enter expedition details..."
                className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("expedition")}
            </div>

            <div data-delivery-move-field="tracking_number">
              <label className={labelClass} htmlFor="service-move-tracking">
                Tracking Number
              </label>
              <input
                id="service-move-tracking"
                type="text"
                value={form.tracking_number}
                onChange={(event) => setField("tracking_number", event.target.value)}
                disabled={submitting}
                placeholder="Enter tracking number..."
                className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("tracking_number")}
            </div>

            <div data-delivery-move-field="notes">
              <label className={labelClass} htmlFor="service-move-notes">
                Note
              </label>
              <textarea
                id="service-move-notes"
                value={form.notes}
                onChange={(event) => setField("notes", event.target.value)}
                disabled={submitting}
                placeholder="Enter note..."
                className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("notes")}
            </div>
            {canEditMoveServiceCost ? <CompactServiceCostSection value={ticket.service_cost} draft={moveServiceCostDraft} onChange={setMoveServiceCostDraft} editable={!submitting} error={errors.service_cost} /> : null}
          </div>

          <div className="flex justify-end gap-2 border-t border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-footer="">
            <Button
              type="button"
              onClick={onClose}
              disabled={submitting}
              variant="cancel"
              data-action-category="cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={submitDisabled}
              variant="success"
              loading={submitting}
              loadingLabel="Submitting..."
              data-action-category="submit-confirm"
            >
              <CircleCheck className="h-4 w-4" strokeWidth={2} />
              Submit
            </Button>
          </div>
        </div>
      </FlowpiDialogSurface>
    );
  }

  if (showValidationFields) {
    return (
      <FlowpiDialogSurface open={open} onClose={onClose} busy={submitting} size="workflow" accessibleTitle={`${stageLabel(currentStage)} to ${stageLabel(targetStage)}`} testId="service-stage-move-dialog">
        <div className="flex max-h-[calc(100vh-32px)] w-full max-w-6xl flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950 sm:max-h-[calc(100vh-48px)]">
          <div className="flex items-start justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800 sm:px-7" data-flowpi-dialog-header="">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                Service Workflow
              </div>
              <h2 className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                {stageLabel(currentStage)} to {stageLabel(targetStage)}
              </h2>
            </div>
            <Button
              type="button"
              onClick={onClose}
              disabled={submitting}
              variant="ghost"
              size="icon"
              data-action-category="close"
              aria-label="Close Move dialog"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-7" data-flowpi-dialog-body="">
            <FormError>{errors.form}</FormError>
            <section>
              <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                Database Info (auto-generated)
              </h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2 md:gap-5">
                <div className="space-y-4">
                  <ReadOnlyMoveField label="Serial Number" value={ticket?.device?.serial_number} />
                  <ReadOnlyMoveField label="Customer" value={ticket?.owner?.name} />
                  <ReadOnlyMoveField
                    label="DVT Warranty Status"
                    value={SERVICE_DVT_STATUS_LABELS[voidWarrantyControl.displayedStatus] || voidWarrantyControl.displayedStatus}
                    labelAdornment={showVoidWarranty ? (
                      <VoidWarrantySwitch
                        id="service-move-void-warranty"
                        checked={form.void_warranty}
                        disabled={voidWarrantyControl.disabled}
                        onChange={updateVoidWarranty}
                      />
                    ) : null}
                  />
                  {showVoidWarranty ? errorText(errors.void_reason ? "void_reason" : "void_warranty") : null}
                </div>
                <div className="space-y-4">
                  <ReadOnlyMoveField label="Device Model" value={ticket?.device?.model} />
                  <ReadOnlyMoveField label="Purchase Date" value={formatDateOnly(ticket?.device?.purchase_date)} />
                  <ReadOnlyMoveField
                    label="iMin Warranty Status"
                    value={IMIN_STATUS_LABELS[ticket?.warranty?.imin?.status] || ticket?.warranty?.imin?.status}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                {showValidationTargetSelector ? "Diagnosis & Action" : "Diagnosis & Repair"}
              </h3>
              <div className="mt-4 grid gap-4 md:grid-cols-6 md:gap-5">
                <div className="md:col-span-3">
                  <label className={labelClass} htmlFor="service-move-problem">Problem</label>
                  <textarea
                    id="service-move-problem"
                    value={form.problem}
                    onChange={(event) => setField("problem", event.target.value)}
                    disabled={submitting}
                    className={`${inputClass} min-h-[100px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
                  />
                  {errorText("problem")}
                </div>
                <div className="md:col-span-3">
                  <label className={labelClass} htmlFor="service-move-root-cause">Root Cause</label>
                  <textarea
                    id="service-move-root-cause"
                    value={form.root_cause}
                    onChange={(event) => setField("root_cause", event.target.value)}
                    disabled={submitting}
                    className={`${inputClass} min-h-[100px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
                  />
                  {errorText("root_cause")}
                </div>
                <div className="md:col-span-6">
                  <label className={labelClass} htmlFor="service-move-resolution">Resolution</label>
                  <ProtectedResolutionField
                    id="service-move-resolution"
                    value={form.resolution}
                    onChange={(value) => setField("resolution", value)}
                    persistedVoid={ticket?.warranty?.dvt?.status === "void"}
                    showLockedSuffix={form.void_warranty}
                    draftReason={form.void_warranty_touched ? form.void_reason : ""}
                    disabled={submitting}
                    placeholder="Describe the resolution applied..."
                    textareaClassName={`${inputClass} min-h-[100px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
                    containerClassName="h-[100px]"
                  />
                  {errorText("resolution")}
                </div>
                {targetStage === "pending" ? (
                  <div className="md:col-span-6">
                    <label className={labelClass} htmlFor="service-move-pending-reason">Pending Reason</label>
                    <textarea
                      id="service-move-pending-reason"
                      value={form.pending_reason}
                      onChange={(event) => setField("pending_reason", event.target.value)}
                      disabled={submitting}
                      placeholder="Describe why this ticket is pending..."
                      className={`${inputClass} min-h-[100px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
                    />
                    {errorText("pending_reason")}
                  </div>
                ) : null}
                <div className="order-1 md:col-span-2">
                  <label className={`${labelClass} flex h-4 items-center`} htmlFor="service-move-repair-action">Repair Action</label>
                  <select
                    id="service-move-repair-action"
                    value={form.repair_action}
                    onChange={(event) => requestRepairAction(event.target.value)}
                    disabled={submitting}
                    className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <option value="">Select Repair Action</option>
                    {REPAIR_ACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errorText("repair_action")}
                </div>
                <CannibalPartSelector
                  ticket={ticket}
                  active={needsSparePartSelection && form.repair_action === "part_replace"}
                  disabled={submitting}
                  onStateChange={setCannibalSelection}
                  fieldClassName="order-2 md:col-span-2"
                  inventoryContent={(cannibalLedger) => (
                    <div
                      className="order-4 md:col-span-6"
                      hidden={form.repair_action !== "part_replace" && !automaticInventoryReversal}
                      onClickCapture={markSparePartPreviewInteraction}
                      data-testid="service-move-spare-part-selector"
                    >
                      <SparePartSelector
                        ticketId={ticket.id}
                        active={needsSparePartSelection}
                        repairAction={form.repair_action}
                        savedRepairAction={ticket.repair_action}
                        disabled={submitting}
                        fieldError={reconciliationError || (form.repair_action === "part_replace" && (requiresValidationFields(currentStage, targetStage) || sparePartSelection.changed) ? sparePartSelection.validationError : "")}
                        onStateChange={handleSparePartStateChange}
                        cannibalLedger={form.repair_action === "part_replace" ? cannibalLedger : null}
                      />
                    </div>
                  )}
                />
                <div className="order-3 md:col-span-2">
                  <label className={`${labelClass} flex h-4 items-center`} htmlFor="service-move-technician">Technician</label>
                  <select
                    id="service-move-technician"
                    value={form.technician_id}
                    onChange={(event) => setField("technician_id", event.target.value)}
                    disabled={submitting || techniciansLoading || Boolean(techniciansError)}
                    className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <option value="">
                      {techniciansLoading ? "Loading technicians..." : "Select Technician"}
                    </option>
                    {technicianOptions.map((technician) => (
                      <option
                        key={technician.id}
                        value={technician.id}
                        disabled={technician.historical === true}
                      >
                        {technician.name}
                      </option>
                    ))}
                  </select>
                  {techniciansError ? (
                    <p className="mt-1 min-h-[18px] text-xs text-destructive">{techniciansError}</p>
                  ) : errorText("technician_id")}
                </div>
              </div>
              {errors.cannibal_device_id || errors.final_cannibal_item_ids || errors.cannibal_selection ? (
                <p className="mt-2 text-xs text-destructive">{errors.cannibal_device_id || errors.final_cannibal_item_ids || errors.cannibal_selection}</p>
              ) : null}
            </section>

            <section>
              <label className={labelClass} htmlFor="service-move-expedition">Expedition</label>
              <input
                id="service-move-expedition"
                value={form.expedition}
                onChange={(event) => setField("expedition", event.target.value)}
                disabled={submitting}
                className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("expedition")}
            </section>

            <section>
              <label className={labelClass} htmlFor="service-move-notes">Notes</label>
              <textarea
                id="service-move-notes"
                value={form.notes}
                onChange={(event) => setField("notes", event.target.value)}
                disabled={submitting}
                className={`${inputClass} min-h-[96px] resize-none disabled:cursor-not-allowed disabled:opacity-60`}
              />
              {errorText("notes")}
            </section>

            {canEditMoveServiceCost ? <CompactServiceCostSection value={ticket.service_cost} draft={moveServiceCostDraft} onChange={setMoveServiceCostDraft} editable={!submitting} error={errors.service_cost} /> : null}

            {showValidationTargetSelector ? (
              <section>
                <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">Stage</h3>
                <div
                  className="mt-3 grid h-11 grid-cols-4 overflow-hidden rounded-sm border border-border bg-muted"
                  role="radiogroup"
                  aria-label="Target Stage"
                  data-testid="service-move-validation-targets"
                >
                  {targets.map((stage) => {
                    const selected = targetStage === stage;
                    return (
                      <label
                        key={stage}
                        data-target-stage={stage}
                        className={`flex min-w-0 cursor-pointer items-center justify-center border-l border-border px-3 text-xs font-semibold uppercase tracking-[0.04em] transition-colors first:border-l-0 focus-within:relative focus-within:z-10 focus-within:outline focus-within:outline-2 focus-within:outline-offset-[-2px] focus-within:outline-primary ${
                          selected
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
                        } ${submitting ? "cursor-not-allowed opacity-60" : ""}`}
                      >
                        <input
                          type="radio"
                          name="service-move-validation-target"
                          value={stage}
                          checked={selected}
                          onChange={() => selectTarget(stage)}
                          disabled={submitting}
                          className="sr-only"
                        />
                        <span className="flex min-w-0 items-center justify-center gap-2">
                          {selected ? (
                            <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden="true" />
                          ) : (
                            <span
                              className="h-4 w-4 shrink-0 rounded-full border border-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                          <span className="truncate">{stageLabel(stage)}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {errorText("target_stage")}
              </section>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-zinc-200 px-5 py-4 dark:border-zinc-800 sm:px-7" data-flowpi-dialog-footer="">
            {usesTargetStep ? (
              <Button
                type="button"
                onClick={backToTargets}
                disabled={submitting}
                variant="navigation-back"
                data-action-category="navigation-back"
              >
                Back
              </Button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={onClose}
                disabled={submitting}
                variant="cancel"
                data-action-category="cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={submitDisabled}
                variant="success"
                loading={submitting}
                loadingLabel="Submitting..."
                data-action-category="submit-confirm"
              >
                <CircleCheck className="h-4 w-4" strokeWidth={2} />
                Submit
              </Button>
            </div>
          </div>
        </div>
        <AlertDialog open={pendingRepairAction !== null} onOpenChange={(nextOpen) => !nextOpen && setPendingRepairAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Change Repair Action?</AlertDialogTitle><AlertDialogDescription>The spare-part draft will be hidden and preserved if you switch back before Submit. A final non-Part-Replace submit reverses active Usage.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => applyRepairAction(pendingRepairAction)}>Continue</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FlowpiDialogSurface>
    );
  }

  return (
    <FlowpiDialogSurface open={open} onClose={onClose} busy={submitting} size="form" accessibleTitle="Move Service Ticket" testId="service-stage-move-dialog">
      <div className="flex max-h-[calc(100vh-48px)] w-full max-w-3xl flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
        <div className="flex items-start justify-between border-b border-zinc-200 px-6 py-4 dark:border-zinc-800" data-flowpi-dialog-header="">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
              Standard Move
            </div>
            <h2 className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              Move Service Ticket
            </h2>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Current stage: {SERVICE_STAGE_LABELS[currentStage] || currentStage || "-"}
            </p>
          </div>
          <Button
            type="button"
            onClick={onClose}
            disabled={submitting}
            variant="ghost"
            size="icon"
            data-action-category="close"
            aria-label="Close Move dialog"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5" data-flowpi-dialog-body="">
          <FormError>{errors.form}</FormError>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="service-move-target">
                Target Stage
              </label>
              <select
                id="service-move-target"
                value={targetStage}
                onChange={(event) => setTargetStage(event.target.value)}
                className={inputClass}
              >
                <option value="">{targets.length > 1 ? "Select target stage" : "Select target"}</option>
                {targets.map((stage) => (
                  <option key={stage} value={stage}>
                    {SERVICE_STAGE_LABELS[stage] || stage}
                  </option>
                ))}
              </select>
              {errorText("target_stage")}
            </div>
            <div>
              <label className={labelClass} htmlFor="service-move-notes">
                Notes
              </label>
              <textarea
                id="service-move-notes"
                value={form.notes}
                onChange={(event) => setField("notes", event.target.value)}
                className={`${inputClass} min-h-[88px] resize-none`}
              />
            </div>
          </div>

          {showProblemRootCause ? (
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="service-move-problem">
                  Problem
                </label>
                <textarea
                  id="service-move-problem"
                  value={form.problem}
                  onChange={(event) => setField("problem", event.target.value)}
                  className={`${inputClass} min-h-[92px] resize-none`}
                />
                {errorText("problem")}
              </div>
              <div>
                <label className={labelClass} htmlFor="service-move-root-cause">
                  Root Cause
                </label>
                <textarea
                  id="service-move-root-cause"
                  value={form.root_cause}
                  onChange={(event) => setField("root_cause", event.target.value)}
                  className={`${inputClass} min-h-[92px] resize-none`}
                />
                {errorText("root_cause")}
              </div>
            </div>
          ) : null}

          {showDeliveryDoneFields ? (
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="service-move-expedition">
                  Expedition
                </label>
                <input
                  id="service-move-expedition"
                  value={form.expedition}
                  onChange={(event) => setField("expedition", event.target.value)}
                  className={inputClass}
                />
                {errorText("expedition")}
              </div>
              <div>
                <label className={labelClass} htmlFor="service-move-tracking">
                  Tracking Number
                </label>
                <input
                  id="service-move-tracking"
                  value={form.tracking_number}
                  onChange={(event) => setField("tracking_number", event.target.value)}
                  className={inputClass}
                />
                {errorText("tracking_number")}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-6 py-4 dark:border-zinc-800 dark:bg-zinc-900" data-flowpi-dialog-footer="">
          <Button
            type="button"
            onClick={onClose}
            disabled={submitting}
            variant="cancel"
            data-action-category="cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitDisabled}
            variant="success"
            loading={submitting}
            loadingLabel="Moving..."
            data-action-category="submit-confirm"
          >
            Move
          </Button>
        </div>
      </div>
      <AlertDialog open={pendingRepairAction !== null} onOpenChange={(nextOpen) => !nextOpen && setPendingRepairAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Repair Action?</AlertDialogTitle>
            <AlertDialogDescription>
              The spare-part draft will be hidden and preserved if you switch back before Submit. A final non-Part-Replace submit reverses active Usage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyRepairAction(pendingRepairAction)}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FlowpiDialogSurface>
  );
}
